/**
 * Centralized, safe, namespaced storage layer.
 * Use for session-scoped and cache data with versioning support.
 */

const STORAGE_PREFIX = 'pragya.';
const STORAGE_VERSION = 1;
const DEBUG_KEY = 'pragya.debug';

function isDebug() {
  try {
    return localStorage.getItem(DEBUG_KEY) === '1';
  } catch {
    return false;
  }
}

function safeGet(key, fallback = null) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key);
    if (raw == null) return fallback;
    const parsed = JSON.parse(raw);
    // Invalidate only when version exists and doesn't match. Preserve unversioned data for backward compat.
    if (parsed?.v !== undefined && parsed?.v !== STORAGE_VERSION && key.startsWith('cache.')) {
      try {
        localStorage.removeItem(STORAGE_PREFIX + key);
      } catch { /* noop */ }
      return fallback;
    }
    return parsed?.v !== undefined ? parsed.data : parsed;
  } catch {
    try {
      localStorage.removeItem(STORAGE_PREFIX + key);
    } catch { /* noop */ }
    if (process.env.VUE_APP_PRAGYA_ENV === 'local') {
      console.warn('[appStorage] get failed:', key);
    }
    return fallback;
  }
}

function safeSet(key, value, versioned = false) {
  try {
    const toStore = versioned ? { v: STORAGE_VERSION, data: value } : value;
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(toStore));
    if (isDebug()) {
      console.log('[appStorage] set:', key);
    }
  } catch (e) {
    if (process.env.VUE_APP_PRAGYA_ENV === 'local') {
      console.warn('[appStorage] set failed:', key, e?.message);
    }
  }
}

function safeRemove(key) {
  try {
    const fullKey = key.startsWith(STORAGE_PREFIX) ? key : STORAGE_PREFIX + key;
    localStorage.removeItem(fullKey);
  } catch { /* noop */ }
}

function safeRemoveFlat(key) {
  try {
    localStorage.removeItem(key);
  } catch { /* noop */ }
}

/**
 * All session-scoped keys that must be cleared on logout.
 * Single source of truth for logout/session cleanup keys.
 * Used by clearSessionStorage() — safe to call standalone (e.g. auth failure).
 */
export const SESSION_KEYS = [
  'breadcrumbHistory', 'token', 'courseId', 'owningLibraryId', 'courseData',
  'contentId', 'contentBody', 'userId', 'studentUserId', 'userInstitutionId',
  'aiAdmin', 'userRole', 'userProfileId', 'assessmentId', 'courseTitle',
  'courseToggle', 'kcPrompt', 'mcPrompt', 'cPrompt', 'kcUserPrompt',
  'kcCustomizationPrompt', 'kcValidationPrompt', 'itemCreationPrompt',
  'createItemValidationPrompt', 'itemCreationFromPrompt', 'createPromptValidationItem',
  'autoContentPrompt', 'extendedCoursePrompt', 'competencyTreePrompt',
  'sourceContextExamTemplate', 'sourceContextConceptTemplate',
  'topicCoverageExamTemplate', 'topicCoverageConceptTemplate',
  'sourceContextDescribeTemplate', 'sourceContextSyllabusTemplate',
  'sourceContextOutcomesTemplate', 'sourceContextStandardsTemplate',
  'sourceContextExamCertTemplate', 'topicCoverageDescribeTemplate',
  'topicCoverageSyllabusTemplate', 'topicCoverageOutcomesTemplate',
  'topicCoverageStandardsTemplate', 'topicCoverageExamCertTemplate',
  'combinePrecedenceInstructions',
  'countConstraintsTemplate', 'pdfContentCustomizationPrompt',
  'ytContentCustomizationPrompt', 'podcastPrompt', 'firstName', 'assessmentData',
  'userEnrolledCourses', 'institutionLogo', 'institutionFolders', 'selectedEntityName',
  'kcActiveTab', 'preCannedQuestionType', 'preCannedQuestions', 'preCannedRails',
  'courseDataResources', 'courseDataKCs', 'aiModel', 'validationModel',
  'validateResponse', 'aiFeature', 'careers',
  'recentReferences', 'savedQuestions', 'pragya_widget_enabled_courses',
  'pragya.itemgen.recentReferences', 'pragya.itemgen.savedQuestions', 'pragya.widget.enabledCourses',
];

/** Clears session-scoped keys from both flat (root store) and prefixed (appStorage) namespaces. */
export function clearSessionStorage() {
  SESSION_KEYS.forEach((k) => {
    safeRemove(k);
    safeRemoveFlat(k);
  });
}

export const appStorage = {
  get: safeGet,
  set: safeSet,
  remove: safeRemove,
};
